import {
	createTopoloAuth,
	decodeJwtPayload,
	getAppId,
	getTokenExpiration,
	isTokenExpired,
	isTokenFullyExpired,
	resolveTopoloBrowserAuthBaseUrl,
} from "@topolo-io/app-shell";
import type { AppUser, AppAuthTokens, TopoloAuth } from "@topolo-io/app-shell";

// Calendar keeps these aliases for its existing import sites; the canonical
// user/token shapes now come from @topolo-io/app-shell.
export type AuthUser = AppUser;
export type AuthTokens = AppAuthTokens;

// Per-tab sessionStorage restore keeps normal browser refreshes signed in.
const sessionStorageRestoreEnabled =
	(import.meta as { env?: { VITE_SESSION_STORAGE_RESTORE?: string } }).env
		?.VITE_SESSION_STORAGE_RESTORE !== "false";

// Calendar derives the Auth origin from the current hostname (auth.stg.topolo.us
// vs auth.topolo.app) when VITE_TOPOLO_AUTH_URL is unset, so there's no hard
// throw in environments that don't bake the env var.
const AUTH_SERVICE_URL = resolveTopoloBrowserAuthBaseUrl();

/**
 * The single canonical Topolo auth instance, built on @topolo-io/app-shell's
 * `createTopoloAuth` (which wraps auth-client's browser session controller).
 * Drives the app-shell TopoloAuthProvider mounted by TopoloAppRoot in App.tsx.
 * Replaces Calendar's previously hand-rolled per-surface auth-client wiring.
 */
export const authClient: TopoloAuth = createTopoloAuth({
	authServiceUrl: AUTH_SERVICE_URL,
	appId: getAppId(),
	devSessionKey: "topolo_calendar_session",
	callbackPath: "/auth/callback",
	sessionStorageRestoreEnabled,
	debug: Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV),
});

export const accessTokenMemory = authClient.accessTokenMemory;
export const accessTokenSession = authClient.accessTokenSession;
export const buildUserFromAccessToken = authClient.buildUserFromAccessToken;

export { decodeJwtPayload, getTokenExpiration, isTokenExpired, isTokenFullyExpired };
